
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { SparklesIcon, UserIcon } from '@heroicons/react/outline';
import { CheckIcon } from '@heroicons/react/solid';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import useLocale from 'next-translate/useTranslation';
import Image from 'next/image';
import Link from 'next/link';
import React, { useState } from 'react';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import { ACTORS } from '@/constants/actors';
import translateReactTree from '@/utils/translateReactTree';
const FEATURES = [
    {
        title: '100+ AI Actors',
        description: 'Choose from a diverse library of realistic AI presenters spanning different ages, ethnicities, and styles to match your brand.',
        icon: '🎭',
    },
    {
        title: '40+ Languages & Accents',
        description: 'Create talking head videos in over 40 languages with natural accents for truly global content creation.',
        icon: '🌍',
    },
    {
        title: 'Natural Lip-Sync',
        description: 'Advanced AI ensures realistic mouth movements perfectly synchronized with your audio for a professional result.',
        icon: '👄',
    },
    {
        title: 'Auto-Generated Captions',
        description: 'Automatically add TikTok-style animated captions to boost engagement and accessibility across platforms.',
        icon: '📝',
    },
    {
        title: 'Multiple Voice Options',
        description: 'Select from hundreds of AI voices or clone your own voice for a truly personalized talking head experience.',
        icon: '🎙️',
    },
    {
        title: 'HD Video Quality',
        description: 'Export talking head videos in up to 720p HD that look professional on any screen or platform.',
        icon: '📺',
    },
    {
        title: 'Custom Script Support',
        description: 'Type, paste, or import your script. Our editor makes it easy to prepare content for your talking head video.',
        icon: '✍️',
    },
    {
        title: 'Fast Generation',
        description: 'Get your talking head videos ready in just minutes, not hours. Iterate quickly and publish faster.',
        icon: '⚡',
    },
    {
        title: 'Commercial Usage Rights',
        description: 'Paid plans include full commercial usage rights, so you can use your talking head videos for business and marketing.',
        icon: '✅',
    },
];
const EXAMPLES_CATEGORIES = [
    {
        key: 'corporate',
        label: 'Corporate Presenter',
        videoUrl: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_9393b702-3e04-4b64-a631-68a6df4c3295.mp4',
        description: 'Corporate communications thrive with a professional talking head format. Training videos, executive updates, product announcements, and onboarding materials all benefit from a polished presenter delivering information clearly. AI talking heads allow companies to produce multilingual content at scale, update videos without reshooting, and maintain brand consistency across global teams.',
    },
    {
        key: 'course',
        label: 'Course Instructor',
        videoUrl: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_03fb0acc-2f7f-4291-a0e7-502de0e8e68f.mp4',
        description: 'Online course creators rely on the talking head format to build personal connections with students. An instructor speaking directly to camera creates trust and engagement that slides alone cannot achieve. With AI talking heads, you can produce entire course libraries in multiple languages, update lessons instantly when content changes, and maintain consistent quality across hundreds of videos.',
    },
    {
        key: 'news',
        label: 'News Anchor',
        videoUrl: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_c19760de-007d-4def-b0d9-55f2ac16c39e.mp4',
        description: 'Talking head videos are the backbone of news broadcasting. A single presenter faces the camera and delivers stories with authority and clarity. AI talking heads let you create news-style content 24/7 without a studio, teleprompter, or camera crew. Perfect for daily briefings, breaking news summaries, and internal company newsletters delivered in a professional broadcast format.',
    },
    {
        key: 'youtube',
        label: 'YouTube Creator',
        videoUrl: 'https://media.easy-peasy.ai/assets/talking-video.webm',
        description: 'The talking head format dominates YouTube because viewers connect with a face. From tech reviews to commentary channels, creators use direct-to-camera presentation to build loyal audiences. AI talking heads enable you to scale content production, create videos in languages you do not speak, and maintain a consistent upload schedule without burnout or expensive equipment.',
    },
];
const USE_CASES = [
    {
        title: 'YouTube Content',
        description: 'Scale your YouTube channel with consistent talking head videos. Create daily uploads in multiple languages without ever sitting in front of a camera.',
        icon: '🎬',
    },
    {
        title: 'Online Courses',
        description: 'Build complete course libraries with professional AI instructors. Update lessons instantly and offer courses in 40+ languages.',
        icon: '🎓',
    },
    {
        title: 'News Broadcasting',
        description: 'Produce news-style talking head segments for daily briefings, internal newsletters, or media channels without a studio.',
        icon: '📰',
    },
    {
        title: 'Corporate Training',
        description: 'Create standardized training videos with consistent presenters across all departments, offices, and languages.',
        icon: '🏢',
    },
    {
        title: 'Product Marketing',
        description: 'Launch product demos and explainer videos with professional presenters that match your brand voice and identity.',
        icon: '📢',
    },
    {
        title: 'Social Media Content',
        description: 'Generate short-form talking head clips for TikTok, Instagram Reels, and LinkedIn with auto-generated captions.',
        icon: '📱',
    },
];
const FAQS = [
    {
        question: 'What is a talking head video?',
        answer: 'A talking head video features a single person speaking directly to the camera, typically from the chest or shoulders up. This format is widely used in YouTube videos, online courses, news broadcasting, corporate training, and social media content. It creates a personal, direct connection between the speaker and the viewer, making it one of the most effective video formats for communication and education.',
    },
    {
        question: 'How do I make a talking head video with AI?',
        answer: 'Making a talking head video with AI is simple: choose an AI presenter from our library of 100+ AI actors, write or paste your script, select a voice and language, then click generate. Our AI will create a realistic talking head video with natural lip-sync, perfect audio, and optional captions in just minutes. No camera, lighting, or studio required.',
    },
    {
        question: 'Can I use my own photo for the talking head?',
        answer: 'Yes! You can upload any portrait photo and our AI will animate it into a realistic talking head video. The AI handles natural lip movements, subtle facial expressions, and head gestures to create a lifelike presentation. This is perfect for creating personalized content or maintaining your own on-screen presence without filming.',
    },
    {
        question: 'What languages are supported?',
        answer: 'We support 40+ languages including English, Spanish, French, German, Portuguese, Chinese, Japanese, Korean, Arabic, Hindi, Italian, Dutch, Russian, Turkish, and many more. Each language comes with multiple accent and voice options so you can create truly localized talking head content for global audiences.',
    },
    {
        question: 'How long can talking head videos be?',
        answer: 'Talking head videos can be up to 5 minutes long per generation, and a typed script can be up to 2,500 characters. For longer content like full course lessons or detailed presentations, you can generate multiple segments and combine them. This approach also gives you more flexibility to edit and rearrange your content.',
    },
    {
        question: 'Can I use talking head videos for commercial purposes?',
        answer: 'Yes, on a paid plan: the talking head videos you generate come with full commercial usage rights. You can use them for business marketing, paid courses, client presentations, social media advertising, YouTube monetization, and any other commercial purpose without additional licensing fees. Videos made on the Free plan are for non-commercial use only.',
    },
];
const WHAT_IS_EXAMPLES = [
    {
        icon: '🎥',
        title: 'YouTube Creators',
        description: 'Solo creators presenting reviews, tutorials, and commentary directly to their audience.',
    },
    {
        icon: '🎓',
        title: 'Online Courses',
        description: 'Instructors teaching lessons with a personal, face-to-face feel that builds student trust.',
    },
    {
        icon: '📰',
        title: 'News & Journalism',
        description: 'Anchors and reporters delivering stories with authority from behind the desk.',
    },
    {
        icon: '🏢',
        title: 'Corporate Training',
        description: 'Professional presenters delivering standardized training across organizations.',
    },
    {
        icon: '⭐',
        title: 'Product Reviews',
        description: 'Reviewers sharing honest opinions about products with their audience face-to-face.',
    },
    {
        icon: '📱',
        title: 'Social Media',
        description: 'Short-form talking head clips that drive engagement on TikTok, Reels, and Shorts.',
    },
];
const STEPS = [
    {
        step: '01',
        title: 'Choose Your Presenter',
        description: 'Pick from 100+ AI actors or upload your own portrait photo. Select the presenter that best matches your brand and content style.',
    },
    {
        step: '02',
        title: 'Write Your Script',
        description: 'Enter your text, select a voice from hundreds of options, choose your language, and customize settings like captions and video format.',
    },
    {
        step: '03',
        title: 'Generate & Download',
        description: 'Click generate and our AI creates a talking head video with perfect lip-sync, natural expressions, and studio-quality output in minutes.',
    },
];
const RELATED_TOOLS = [
    {
        title: 'AI Spokesperson Videos',
        description: 'Create professional spokesperson videos with AI presenters for marketing and communications.',
        href: '/ai-video-generator/spokesperson',
    },
    {
        title: 'Make Image Talk',
        description: 'Upload any photo and bring it to life with realistic speech and facial animations.',
        href: '/ai-video-generator/make-image-talk',
    },
    {
        title: 'AI Talking Avatar',
        description: 'Generate custom talking avatars for interactive content, chatbots, and virtual assistants.',
        href: '/ai-video-generator/talking-avatar',
    },
    {
        title: 'AI Video Generator',
        description: 'Explore our full suite of AI video generation tools for every content need.',
        href: '/ai-video-generator',
    },
];
export default function TalkingHeadLanding() {
    const { t } = useLocale('ai-video-talking-head');
    const [activeTab, setActiveTab] = useState('news');
    const professionalActors = ACTORS.filter((actor) => actor.category === 'professional').slice(0, 12);
    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
            },
        })),
    };
    const webAppSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Easy-Peasy.AI Talking Head Video Generator',
        description: 'Create professional AI talking head videos in minutes. No camera needed. Choose from 100+ AI presenters, 40+ languages.',
        url: 'https://easy-peasy.ai/ai-video-generator/talking-head',
        applicationCategory: 'MultimediaApplication',
        operatingSystem: 'Web',
        offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD',
        },
        featureList: [
            '100+ AI Presenters',
            '40+ Languages',
            'Natural Lip-Sync',
            'Auto-Generated Captions',
            'HD Video Quality',
            'Commercial Rights on Paid Plans',
        ],
    };
    const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: 'https://easy-peasy.ai',
            },
            {
                '@type': 'ListItem',
                position: 2,
                name: 'AI Video Generator',
                item: 'https://easy-peasy.ai/ai-video-generator',
            },
            {
                '@type': 'ListItem',
                position: 3,
                name: 'Talking Head Video Generator',
                item: 'https://easy-peasy.ai/ai-video-generator/talking-head',
            },
        ],
    };
    return translateReactTree(<>
      <Seo templateTitle={t('AI Talking Head Video Generator | Create Professional Talking Head Videos')} description={t('Create professional AI talking head videos in minutes. No camera needed. Choose from 100+ AI presenters, 40+ languages. Perfect for YouTube, courses, news & marketing.')}/>
      <Head>
        <link rel='canonical' href='https://easy-peasy.ai/ai-video-generator/talking-head'/>
        <meta name='keywords' content='ai talking head video generator, what is a talking head video, how to make a talking head video, talking head video examples, ai video generator, talking head creator'/>
        <meta name='robots' content='index, follow'/>
        <meta property='og:title' content='AI Talking Head Video Generator | Easy-Peasy.AI'/>
        <meta property='og:description' content='Create professional AI talking head videos in minutes. No camera needed. Choose from 100+ AI presenters, 40+ languages.'/>
        <meta property='og:type' content='website'/>
        <meta property='og:url' content='https://easy-peasy.ai/ai-video-generator/talking-head'/>
        <meta name='twitter:card' content='summary_large_image'/>
        <meta name='twitter:title' content='AI Talking Head Video Generator | Easy-Peasy.AI'/>
        <meta name='twitter:description' content='Create professional AI talking head videos in minutes. No camera needed. Choose from 100+ AI presenters, 40+ languages.'/>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(webAppSchema) }}/>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}/>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}/>
      </Head>

      <style jsx global>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        @keyframes float {
          0% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
          100% {
            transform: translateY(0px);
          }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      <div className='min-h-screen bg-gradient-to-b from-blue-50 via-white to-indigo-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950'>
        <LandingPageNavigation />

        {/* Background Orbs */}
        <div className='pointer-events-none fixed inset-0 overflow-hidden'>
          <div className='animate-blob absolute -left-40 top-0 h-96 w-96 rounded-full bg-blue-300/20 mix-blend-multiply blur-3xl filter dark:bg-blue-900/10'/>
          <div className='animate-blob animation-delay-2000 absolute -right-40 top-48 h-96 w-96 rounded-full bg-indigo-300/20 mix-blend-multiply blur-3xl filter dark:bg-indigo-900/10'/>
          <div className='animate-blob animation-delay-4000 absolute -bottom-40 left-1/2 h-96 w-96 rounded-full bg-cyan-300/20 mix-blend-multiply blur-3xl filter dark:bg-cyan-900/10'/>
          <div className='absolute inset-0 opacity-[0.015] dark:opacity-[0.03]' style={{
            backgroundImage: 'linear-gradient(rgba(99,102,241,.8) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,.8) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
        }}/>
        </div>

        {/* ===== HERO SECTION ===== */}
        <section className='relative overflow-hidden pb-20 pt-32 sm:pt-40'>
          <div className='relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='grid items-center gap-12 lg:grid-cols-2 lg:gap-16'>
              {/* Left — text */}
              <div className='text-center lg:text-left'>
                {/* Badge */}
                <div className='animate-fadeInUp mb-8 inline-flex items-center gap-2 rounded-full border border-blue-200/60 bg-white/70 px-5 py-2.5 shadow-lg shadow-blue-500/5 backdrop-blur-sm dark:border-blue-800/40 dark:bg-gray-800/70'>
                  <SparklesIcon className='h-4 w-4 text-blue-600 dark:text-blue-400'/>
                  <span className='text-sm font-medium text-blue-700 dark:text-blue-300'>
                    AI-Powered Talking Head Videos
                  </span>
                </div>

                {/* H1 */}
                <h1 className='animate-fadeInUp text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-6xl lg:text-7xl'>
                  Create Professional
                  <span className='mt-2 pb-2 block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400'>
                    Talking Head Videos
                  </span>
                </h1>

                {/* Subtitle */}
                <p className='animate-fadeInUp mt-8 max-w-2xl text-lg leading-relaxed text-gray-600 dark:text-gray-300 sm:text-xl'>
                  Create studio-quality talking head videos without a camera or
                  studio. Choose from 100+ AI presenters, 40+ languages, and get
                  professional results in minutes.
                </p>

                {/* CTAs */}
                <div className='animate-fadeInUp mt-10 flex flex-col items-center gap-4 sm:flex-row lg:justify-start'>
                  <ButtonLink href='/ai-talking-videos' className='rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-blue-500/25 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/30 dark:shadow-blue-800/30'>
                    Create Talking Head Video
                  </ButtonLink>
                  <a href='#examples' className='group inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white/80 px-8 py-4 text-lg font-semibold text-gray-700 backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:border-blue-300 hover:text-blue-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-200 dark:hover:border-blue-600 dark:hover:text-blue-400'>
                    See Examples
                    <svg className='h-4 w-4 transition-transform group-hover:translate-y-0.5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7'/>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Right — two videos side by side */}
              <div className='flex justify-center gap-4 lg:justify-end'>
                {[
            {
                src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_b4cc830f-c5ca-435e-8da0-05a0049898aa.mp4',
                label: 'Male Presenter',
            },
            {
                src: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_b5ef5f5c-cd31-42ef-864b-ddbc3a867a84.mp4',
                label: 'Female Presenter',
            },
        ].map((video) => (<div key={video.label} className='relative w-full max-w-[260px] overflow-hidden rounded-2xl border border-blue-200/40 shadow-2xl shadow-blue-500/10 dark:border-blue-800/30'>
                    <div className='relative aspect-[9/16] bg-gray-100 dark:bg-gray-800'>
                      <video autoPlay muted loop playsInline className='h-full w-full object-cover' src={video.src}/>
                    </div>
                    <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 pb-3 pt-8'>
                      <span className='text-xs font-medium text-white/90'>
                        {video.label}
                      </span>
                    </div>
                  </div>))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== LOGOS CLOUD ===== */}
        <section className='relative py-12'>
          <LogosCloud />
        </section>

        {/* ===== WHAT IS A TALKING HEAD VIDEO? ===== */}
        <section className='relative py-20 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='mx-auto max-w-3xl text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                What Is a{' '}
                <span className='bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400'>
                  Talking Head Video
                </span>
                ?
              </h2>
              <p className='mt-6 text-lg leading-relaxed text-gray-600 dark:text-gray-300'>
                A talking head video features a single person speaking directly
                to the camera, typically framed from the chest or shoulders up.
                It is the most popular format on YouTube, online courses, news
                broadcasting, and social media because it creates a direct,
                personal connection between the speaker and viewer. With AI, you
                can now create talking head videos without filming a single
                frame.
              </p>
            </div>

            <div className='mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
              {WHAT_IS_EXAMPLES.map((example) => (<div key={example.title} className='group relative overflow-hidden rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl dark:border-gray-800 dark:bg-gray-800/80'>
                  <div className='absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100'/>
                  <div className='relative'>
                    <span className='text-3xl'>{example.icon}</span>
                    <h3 className='mt-4 text-lg font-semibold text-gray-900 dark:text-white'>
                      {example.title}
                    </h3>
                    <p className='mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400'>
                      {example.description}
                    </p>
                  </div>
                </div>))}
            </div>
          </div>
        </section>

        {/* ===== FEATURES ===== */}
        <section className='relative py-20 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='mx-auto max-w-3xl text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                Everything You Need for{' '}
                <span className='bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400'>
                  Talking Head Videos
                </span>
              </h2>
              <p className='mt-6 text-lg text-gray-600 dark:text-gray-300'>
                Professional tools to create studio-quality talking head videos
                powered by cutting-edge AI technology.
              </p>
            </div>

            <div className='mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
              {FEATURES.map((feature) => (<div key={feature.title} className='group relative overflow-hidden rounded-2xl border border-gray-100 bg-white/80 p-8 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl dark:border-gray-800 dark:bg-gray-800/80'>
                  {/* Shine overlay */}
                  <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full'/>
                  <div className='relative'>
                    <span className='text-4xl'>{feature.icon}</span>
                    <h3 className='mt-5 text-lg font-semibold text-gray-900 dark:text-white'>
                      {feature.title}
                    </h3>
                    <p className='mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400'>
                      {feature.description}
                    </p>
                  </div>
                </div>))}
            </div>
          </div>
        </section>

        {/* ===== TALKING HEAD VIDEO EXAMPLES ===== */}
        <section id='examples' className='relative py-20 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='mx-auto max-w-3xl text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                Talking Head{' '}
                <span className='bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400'>
                  Video Examples
                </span>
              </h2>
              <p className='mt-6 text-lg text-gray-600 dark:text-gray-300'>
                Explore how talking head videos are used across different
                industries and content types.
              </p>
            </div>

            <div className='mt-12'>
              {/* Tabs */}
              <div className='flex flex-wrap justify-center gap-3'>
                {EXAMPLES_CATEGORIES.map((cat) => (<button key={cat.key} onClick={() => setActiveTab(cat.key)} className={`rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-300 ${activeTab === cat.key
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25'
                : 'border border-gray-200 bg-white/80 text-gray-600 hover:border-blue-300 hover:text-blue-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-300 dark:hover:border-blue-600'}`}>
                    {cat.label}
                  </button>))}
              </div>

              {/* Tab Content */}
              <div className='mt-10'>
                {EXAMPLES_CATEGORIES.map((cat) => activeTab === cat.key ? (<div key={cat.key} className='mx-auto max-w-3xl overflow-hidden rounded-2xl border border-gray-100 bg-white/80 shadow-sm backdrop-blur-sm dark:border-gray-800 dark:bg-gray-800/80'>
                      {/* Video Player */}
                      <div className='relative aspect-video bg-gray-100 dark:bg-gray-900'>
                        <video key={cat.key} loop playsInline controls className='h-full w-full object-cover' src={cat.videoUrl}>
                          Your browser does not support the video tag.
                        </video>
                      </div>
                      {/* Description */}
                      <div className='p-8 sm:p-10'>
                        <div className='flex items-center gap-3'>
                          <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg'>
                            <UserIcon className='h-5 w-5 text-white'/>
                          </div>
                          <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
                            {cat.label}
                          </h3>
                        </div>
                        <p className='mt-4 text-base leading-relaxed text-gray-600 dark:text-gray-300'>
                          {cat.description}
                        </p>
                        <div className='mt-6'>
                          <ButtonLink href='/ai-talking-videos' className='rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:scale-105 hover:shadow-lg'>
                            Create {cat.label} Video
                          </ButtonLink>
                        </div>
                      </div>
                    </div>) : null)}
              </div>
            </div>
          </div>
        </section>

        {/* ===== HOW IT WORKS ===== */}
        <section className='relative py-20 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='mx-auto max-w-3xl text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                How to Make a{' '}
                <span className='bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400'>
                  Talking Head Video
                </span>
              </h2>
              <p className='mt-6 text-lg text-gray-600 dark:text-gray-300'>
                Create professional talking head videos in three simple steps.
                No filming required.
              </p>
            </div>

            <div className='mt-16 grid gap-8 lg:grid-cols-3'>
              {STEPS.map((step, index) => (<div key={step.step} className='relative'>
                  {/* Connector line */}
                  {index < STEPS.length - 1 && (<div className='absolute left-1/2 top-24 hidden h-0.5 w-full bg-gradient-to-r from-blue-200 to-indigo-200 dark:from-blue-800 dark:to-indigo-800 lg:block'/>)}
                  <div className='group relative overflow-hidden rounded-2xl border border-gray-100 bg-white/80 p-8 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl dark:border-gray-800 dark:bg-gray-800/80'>
                    <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full'/>
                    <div className='relative'>
                      <div className='mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-xl font-bold text-white shadow-lg'>
                        {step.step}
                      </div>
                      <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
                        {step.title}
                      </h3>
                      <p className='mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400'>
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>))}
            </div>

            <div className='mt-12 text-center'>
              <ButtonLink href='/ai-talking-videos' className='rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-blue-500/25 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/30'>
                Start Creating Now
              </ButtonLink>
            </div>
          </div>
        </section>

        {/* ===== AI ACTORS SECTION ===== */}
        <section className='relative py-20 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='mx-auto max-w-3xl text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                Professional{' '}
                <span className='bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400'>
                  AI Presenters
                </span>
              </h2>
              <p className='mt-6 text-lg text-gray-600 dark:text-gray-300'>
                Choose from our curated library of professional AI actors to
                deliver your talking head videos with confidence and style.
              </p>
            </div>

            <div className='mt-16 grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'>
              {professionalActors.map((actor) => (<div key={actor.id} className='group relative overflow-hidden rounded-2xl border border-gray-100 bg-white/80 shadow-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl dark:border-gray-800 dark:bg-gray-800/80'>
                  {/* Image */}
                  <div className='relative aspect-[3/4] w-full overflow-hidden'>
                    <Image src={actor.photo} alt={actor.name} layout='fill' objectFit='cover' className='transition-transform duration-500 group-hover:scale-110'/>
                    {/* Gradient overlay */}
                    <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent'/>
                    {/* Shine overlay */}
                    <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full'/>
                    {/* Name overlay */}
                    <div className='absolute bottom-0 left-0 right-0 p-4'>
                      <h3 className='text-lg font-bold text-white'>
                        {actor.name}
                      </h3>
                      {actor.description && (<p className='mt-1 text-sm text-gray-200'>
                          {actor.description}
                        </p>)}
                    </div>
                  </div>
                </div>))}
            </div>

            <div className='mt-12 text-center'>
              <ButtonLink href='/ai-talking-videos' className='rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-blue-500/25 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-blue-500/30'>
                Try AI Presenters Now
              </ButtonLink>
            </div>
          </div>
        </section>

        {/* ===== USE CASES ===== */}
        <section className='relative py-20 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='mx-auto max-w-3xl text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                Talking Head Videos for{' '}
                <span className='bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400'>
                  Every Use Case
                </span>
              </h2>
              <p className='mt-6 text-lg text-gray-600 dark:text-gray-300'>
                From YouTube to corporate training, talking head videos are the
                most versatile content format for communication.
              </p>
            </div>

            <div className='mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
              {USE_CASES.map((useCase) => (<div key={useCase.title} className='group relative overflow-hidden rounded-2xl border border-gray-100 bg-white/80 p-8 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl dark:border-gray-800 dark:bg-gray-800/80'>
                  <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full'/>
                  <div className='relative'>
                    <span className='text-4xl'>{useCase.icon}</span>
                    <h3 className='mt-5 text-lg font-semibold text-gray-900 dark:text-white'>
                      {useCase.title}
                    </h3>
                    <p className='mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400'>
                      {useCase.description}
                    </p>
                  </div>
                </div>))}
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className='relative py-20 sm:py-28'>
          <div className='mx-auto max-w-3xl px-4 sm:px-6 lg:px-8'>
            <div className='text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl lg:text-5xl'>
                Frequently Asked{' '}
                <span className='bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400'>
                  Questions
                </span>
              </h2>
              <p className='mt-6 text-lg text-gray-600 dark:text-gray-300'>
                Everything you need to know about creating talking head videos
                with AI.
              </p>
            </div>

            <div className='mt-12 space-y-4'>
              {FAQS.map((faq, index) => (<details key={index} className='group rounded-2xl border border-gray-100 bg-white/80 shadow-sm backdrop-blur-sm transition-all duration-300 hover:shadow-md dark:border-gray-800 dark:bg-gray-800/80 [&_summary::-webkit-details-marker]:hidden'>
                  <summary className='flex cursor-pointer items-center justify-between p-6'>
                    <h3 className='pr-4 text-lg font-semibold text-gray-900 dark:text-white'>
                      {faq.question}
                    </h3>
                    <svg className='h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-300 group-open:rotate-180 dark:text-gray-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7'/>
                    </svg>
                  </summary>
                  <div className='border-t border-gray-100 px-6 pb-6 pt-4 dark:border-gray-700'>
                    <p className='text-base leading-relaxed text-gray-600 dark:text-gray-300'>
                      {faq.answer}
                    </p>
                  </div>
                </details>))}
            </div>
          </div>
        </section>

        {/* ===== PRICING CTA ===== */}
        <section className='relative py-20 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 px-8 py-16 shadow-2xl sm:px-16 sm:py-20'>
              {/* Background pattern */}
              <div className='absolute inset-0 opacity-10'>
                <div className='h-full w-full' style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '32px 32px',
        }}/>
              </div>
              <div className='relative mx-auto max-w-3xl text-center'>
                <h2 className='text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl'>
                  Start Creating Talking Head Videos Today
                </h2>
                <p className='mt-6 text-lg text-blue-100'>
                  Join thousands of creators using AI to produce professional
                  talking head content.
                </p>

                <div className='mt-10 flex flex-wrap justify-center gap-4'>
                  {[
            '100+ AI Presenters',
            '40+ Languages',
            'Natural Lip-Sync',
            'HD Video Quality',
            'Commercial Rights on Paid Plans',
            'No Camera Needed',
        ].map((feature) => (<div key={feature} className='flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 backdrop-blur-sm'>
                      <CheckIcon className='h-4 w-4 text-blue-200'/>
                      <span className='text-sm font-medium text-white'>
                        {feature}
                      </span>
                    </div>))}
                </div>

                <div className='mt-10'>
                  <ButtonLink href='/ai-talking-videos' className='rounded-xl bg-white px-8 py-4 text-lg font-semibold text-blue-700 shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl'>
                    Create Your First Talking Head Video
                  </ButtonLink>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== RELATED AI VIDEO TOOLS ===== */}
        <section className='relative py-20 sm:py-28'>
          <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
            <div className='mx-auto max-w-3xl text-center'>
              <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
                Related{' '}
                <span className='bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent dark:from-blue-400 dark:to-indigo-400'>
                  AI Video Tools
                </span>
              </h2>
              <p className='mt-4 text-lg text-gray-600 dark:text-gray-300'>
                Explore more AI-powered video generation tools to create any
                type of video content.
              </p>
            </div>

            <div className='mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4'>
              {RELATED_TOOLS.map((tool) => (<Link key={tool.href} href={tool.href} className='group relative overflow-hidden rounded-2xl border border-gray-100 bg-white/80 p-6 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 hover:shadow-xl dark:border-gray-800 dark:bg-gray-800/80'>
                  <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full'/>
                  <div className='relative'>
                    <h3 className='text-lg font-semibold text-gray-900 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400'>
                      {tool.title}
                    </h3>
                    <p className='mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400'>
                      {tool.description}
                    </p>
                    <span className='mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400'>
                      Learn more
                      <svg className='h-4 w-4 transition-transform group-hover:translate-x-1' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7'/>
                      </svg>
                    </span>
                  </div>
                </Link>))}
            </div>
          </div>
        </section>

        {/* ===== SHARED COMPONENTS ===== */}
        <FeaturedLogos />
        <Testimonials />
        <CallToAction preset='ai-talking-videos'/>
        <Footer />
      </div>
    </>, t);
}
const getStaticProps: GetStaticProps = async () => {
    return {
        props: {},
    };
};

    async function __Next_Translate__getStaticProps__1a12955d49f__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-video-generator/talking-head',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a12955d49f__ as getStaticProps }
  